import { memo, useCallback, useEffect, useMemo, useState } from "react";

import {
  DeleteOutlineOutlined,
  EmojiSymbols,
  MoreHorizRounded,
} from "@mui/icons-material";
import CheckIcon from "@mui/icons-material/Check";
import type { SxProps } from "@mui/material";
import {
  Box,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
  TooltipProps,
  Typography,
} from "@mui/material";

import {
  DropdownMenuItem,
  DropdownNestedMenuItem,
} from "@/components/DropDownMenuItem";
import TextField from "@/components/TextField";
import emoticons from "@/components/TextField/emoticons";

import {
  DEBOUNCE,
  Editable,
  Item,
} from "@/extensions/obituary/pages/detail/Story/shared";
import type { Emoticon } from "@/extensions/obituary/types";

import { useStory } from "../context";

import EditableItem from "./EditableItem";
import { RelativeVariant, relativeVariantOptions } from "./Multiline";

import * as object from "@/utils/object";

export type Variant = "heading" | "author" | "url" | RelativeVariant;

type VariantOption = { value: Variant; label: string };

const baseVariantOptions: VariantOption[] = [
  { value: "heading", label: "Namn" },
  { value: "author", label: "Författare" },
  { value: "url", label: "Webbsida" },
];

const relativeVariantOptionsForSingleline: VariantOption[] =
  relativeVariantOptions.map(({ label, value }) => ({
    label,
    value: value as Variant,
  }));

const allVariantOptions: VariantOption[] = [
  ...baseVariantOptions,
  ...relativeVariantOptionsForSingleline,
];

export const variants = baseVariantOptions;

export interface Singleline extends Item {
  type: "singleline";
  variant?: Variant;
  text: string;
}

export function isSingleline(item: Item): item is Singleline {
  return item.type === "singleline";
}

export function isVariant(value: unknown): value is Variant {
  return allVariantOptions.some((option) => option.value === value);
}

export function makeSingleline(singleline: Partial<Singleline>): Singleline {
  return {
    id: crypto.randomUUID(),
    type: "singleline",
    text: "",
    ...singleline,
  };
}

type BaseProps = Parameters<Editable<Singleline>>[0];

interface TableCellMeta {
  columnIndex: number;
  columnCount: number;
  rowIndex: number;
  rowCount: number;
  hasLeftNeighbor?: boolean;
}

interface SinglelineProps extends BaseProps {
  hideVariantButton?: boolean;
  tableCell?: TableCellMeta;
}

const Edit: React.FC<SinglelineProps> = ({
  item,
  path,
  noHeader,
  tableCell,
}) => {
  const { change, getContainerVariants } = useStory();
  const { hidden, text, variant, id } = item;

  const title = useMemo(() => {
    const label =
      allVariantOptions.find((option) => option.value === variant)?.label ??
      "Fritext";
    return hidden ? `${label} (dold)` : label;
  }, [hidden, variant]);

  const variantLabel = useMemo(() => {
    return (
      allVariantOptions.find((option) => option.value === variant)?.label ??
      "Fritext"
    );
  }, [variant]);

  const [textFieldKey, setTextFieldKey] = useState(0);
  const isAuthor = variant === "author";

  const containerVariants = useMemo(
    () => getContainerVariants?.(path) ?? [],
    [getContainerVariants, path],
  );
  const showVariantLabel = useMemo(
    () =>
      containerVariants.some((containerVariant) =>
        containerVariant?.includes("relatives"),
      ),
    [containerVariants],
  );

  useEffect(() => {
    if (!isAuthor || text == null) return;
    setTextFieldKey((key) => key + 1);
  }, [isAuthor, text]);

  // Callbacks

  const handleChangeText = useCallback(
    (text: string) => change<Singleline>(path, { text }),
    [change, path],
  );

  const isTableCell = tableCell != null;

  const cellBorders = useMemo(() => {
    if (tableCell == null) return null;

    const { columnIndex, columnCount, rowIndex, rowCount } = tableCell;
    const isFirstColumn = columnIndex === 0;
    const isLastColumn = columnIndex === columnCount - 1;
    const isFirstRow = rowIndex === 0;
    const isLastRow = rowIndex === rowCount - 1;
    const hasLeftNeighbor = tableCell.hasLeftNeighbor ?? !isFirstColumn;
    const needsLeftBorder = isFirstColumn || !hasLeftNeighbor;

    return {
      border: "1px solid",
      borderColor: "divider",
      borderLeftWidth: needsLeftBorder ? 1 : 0,
      borderRightWidth: 1,
      borderTopWidth: isFirstRow ? 1 : 0,
      borderBottomWidth: 1,
      borderTopLeftRadius: isFirstColumn && isFirstRow ? 8 : 0,
      borderTopRightRadius: isLastColumn && isFirstRow ? 8 : 0,
      borderBottomLeftRadius: isFirstColumn && isLastRow ? 8 : 0,
      borderBottomRightRadius: isLastColumn && isLastRow ? 8 : 0,
    };
  }, [tableCell]);

  const Field = (
    <Box
      sx={{
        position: "relative",
        ...(cellBorders ?? {}),
        ...(isTableCell
          ? {
              overflow: "hidden",
              backgroundColor: "background.paper",
            }
          : {}),
        ...(showVariantLabel
          ? {
              px: 1.5,
              pt: 0.8,
              pb: 0.8,
            }
          : {}),
      }}
    >
      {!hidden && showVariantLabel && (
        <Typography
          className="singleline-variant-label"
          sx={{
            zIndex: 1,
            position: "absolute",
            fontSize: "0.75rem",
            color: "text.secondary",
            pointerEvents: "none",
            transition: "opacity 100ms",
          }}
          variant="caption"
        >
          {variantLabel}
        </Typography>
      )}

      <TextField
        key={textFieldKey}
        debounce={DEBOUNCE}
        disabled={Boolean(hidden)}
        id={id}
        name="text"
        rows={2}
        sx={{
          ...(isTableCell
            ? {
                "& .MuiOutlinedInput-root": {
                  borderRadius: 0,
                  "& fieldset": { border: "none" },
                },
              }
            : {}),
          "& .MuiOutlinedInput-input": {
            ...(showVariantLabel
              ? {
                  p: 0,
                  pt: 2.5,
                }
              : {}),
          },
        }}
        value={text}
        variant="outlined"
        onChange={handleChangeText}
      />
    </Box>
  );

  return noHeader ? (
    Field
  ) : (
    <EditableItem actions="mutate" hidden={hidden} path={path} title={title}>
      {!hidden && Field}
    </EditableItem>
  );
};

export interface VariantButtonProps {
  className?: string;
  hidden?: boolean;
  item: Singleline;
  path: object.Path;
  tooltipPlacement?: TooltipProps["placement"];
  handleDelete: () => void;
}

export const SinglelineVariantButton: React.FC<VariantButtonProps> = ({
  className,
  hidden,
  item,
  path,
  tooltipPlacement = "top",
  handleDelete,
}) => {
  const { variant, text } = item;
  const { change, getContainerVariants } = useStory();
  const [singlelineOptionsAnchorEl, setSinglelineOptionsAnchorEl] =
    useState<null | HTMLElement>(null);

  const containerVariants = useMemo(
    () => getContainerVariants(path),
    [getContainerVariants, path],
  );

  const isRelative = useMemo(
    () =>
      containerVariants.some((containerVariant) =>
        containerVariant?.toString().includes("relative"),
      ),
    [containerVariants],
  );

  const variantOptions = useMemo<VariantOption[]>(
    () =>
      isRelative ? relativeVariantOptionsForSingleline : baseVariantOptions,
    [isRelative],
  );

  const showVariantButton = variantOptions.length > 0 && !hidden;

  const handleOpenSinglelineOptionsMenu = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      setSinglelineOptionsAnchorEl(event.currentTarget);
    },
    [],
  );

  const handleCloseSinglelineOptionsMenu = useCallback(() => {
    setSinglelineOptionsAnchorEl(null);
  }, []);

  const handleVariantSelect = useCallback(
    (nextVariant: string) => () => {
      if (variant === nextVariant) {
        return;
      }

      if (!isVariant(nextVariant)) {
        console.error("Invalid variant", nextVariant);
        return;
      }

      change<Singleline>(path, { variant: nextVariant });
      handleCloseSinglelineOptionsMenu();
    },
    [change, path, variant],
  );

  const handleInsertEmoticon = useCallback(
    (emoticon: Emoticon) => () => {
      change<Singleline>(path, { text: `${text}${emoticon.code}` });
      handleCloseSinglelineOptionsMenu();
    },
    [change, path, text, handleCloseSinglelineOptionsMenu],
  );

  if (!showVariantButton) return null;

  const baseButtonSx: SxProps = {
    bgcolor: "background.paper",
    border: "1px solid",
    borderColor: "grey.400",
    width: 28,
    height: 28,
    p: 0,
    "&:hover": {
      borderColor: "text.primary",
      bgcolor: "background.paper",
    },
  };

  return (
    <>
      <Tooltip arrow placement={tooltipPlacement} title="Meny">
        <IconButton
          className={className}
          size="small"
          sx={baseButtonSx}
          tabIndex={-1}
          onClick={handleOpenSinglelineOptionsMenu}
        >
          <MoreHorizRounded fontSize="small" />
        </IconButton>
      </Tooltip>

      <Menu
        anchorEl={singlelineOptionsAnchorEl}
        open={Boolean(singlelineOptionsAnchorEl)}
        onClose={handleCloseSinglelineOptionsMenu}
      >
        {/* All variants */}
        {variantOptions.map(({ value, label }) => (
          <MenuItem
            key={value}
            selected={value === variant}
            onClick={handleVariantSelect(value)}
          >
            <ListItemIcon
              sx={{ visibility: value === variant ? "visible" : "hidden" }}
            >
              <CheckIcon fontSize="small" />
            </ListItemIcon>

            <ListItemText primary={label} />
          </MenuItem>
        ))}

        <Divider />

        <DropdownNestedMenuItem
          label={
            <>
              <ListItemIcon>
                <EmojiSymbols fontSize="small" />
              </ListItemIcon>

              <ListItemText primary="Ikoner" />
            </>
          }
          parentMenuOpen={Boolean(singlelineOptionsAnchorEl)}
        >
          {emoticons.map((emoticon) => (
            <DropdownMenuItem
              key={emoticon.code}
              onClick={handleInsertEmoticon(emoticon)}
            >
              <ListItemIcon sx={{ minWidth: 36 }}>
                {emoticon.image ? (
                  <Box
                    alt={emoticon.name}
                    component="img"
                    src={`/emoticons/${emoticon.image}`}
                    sx={{
                      width: 28,
                      height: 28,
                      background: "white",
                      borderRadius: "50%",
                      p: 0.25,
                    }}
                  />
                ) : (
                  <Typography fontSize="1rem">{emoticon.char}</Typography>
                )}
              </ListItemIcon>
              <ListItemText primary={emoticon.name} />
            </DropdownMenuItem>
          ))}
        </DropdownNestedMenuItem>

        <Divider sx={{ my: 1 }} />

        <MenuItem sx={{ color: "error.main" }} onClick={handleDelete}>
          <ListItemIcon sx={{ color: "inherit" }}>
            <DeleteOutlineOutlined fontSize="small" />
          </ListItemIcon>

          <ListItemText
            primary="Ta bort"
            primaryTypographyProps={{ color: "inherit" }}
          />
        </MenuItem>
      </Menu>
    </>
  );
};

export default memo(Edit);
